import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { AccountTouchPointSummary, SessionSummary } from '@eucoms/customer-touchpoints-tracker';

@Component({
  selector: 'app-customer-alert-cxw',
  templateUrl: './customer-alert-cxw.component.html',
  styleUrls: ['./customer-alert-cxw.component.scss']
})
export class CustomerAlertCxwComponent implements OnInit {

  @Input() summary: AccountTouchPointSummary | null = null;
  @Input() opCo: string = '';
  @Input() feedbackDisabled: boolean = false; // provided by parent
  @Input() feedbackState: { [key: string]: 'up' | 'down' | null } = {};
  @Output() feedbackChange = new EventEmitter<{ sessionId: string, resultCode: string, state: 'up' | 'down' | null }>();

  isPanelVisible: boolean = false;
  isPanelViewed: boolean = false;
  constructor() { }

  // Handle feedback emitted from child component
  onFeedbackChange(event: { sessionId: string, resultCode: string, state: 'up' | 'down' | null }) {
    this.feedbackChange.emit(event);
  }

  getFeedbackStateFor(session: SessionSummary): 'up' | 'down' | null {
    const key = `${session.sessionId}_${session.resultCode ?? ''}`;
    return this.feedbackState[key] || null;
  }

  // Helper to get session id
  getSessionId(session: SessionSummary): string {
    return session.sessionId;
  }

  ngOnInit(): void {
    console.log('summary = ', this.summary);
  }

  openSummaryPanel(): void {
    this.isPanelVisible = true;
  }

  closeSummaryPanel(): void {
    this.isPanelVisible = false;
    this.isPanelViewed = true;
  }

  allSessionSummariesOlderThan30Days(): boolean {
    if (this.summary?.sessionSummaries?.length > 0) {
      return this.summary.sessionSummaries.every(
        (sessionSummary: any) => this.thisSessionSummaryOlderThan30Days(sessionSummary.lastUpdated)
      );
    }
    // If there are no session summaries, treat as true (all are "older")
    return true;
  }

  thisSessionSummaryOlderThan30Days(lastUpdated?: any): boolean {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.setDate(now.getDate() - 30));
    return lastUpdated && new Date(lastUpdated) < thirtyDaysAgo;
  }

  getAccountSummaryText(summary?: AccountTouchPointSummary): string {
    if (summary) {
      if (summary.summaryText?.length > 0) {
        if (this.allSessionSummariesOlderThan30Days()) {
          // If there are session summaries, but they are older than 30 days, display this text
          return 'No customer self-service activity in the last 30 days.';
        }
        // Display standard account summary text if it exists and at least one of the session summaries are recent
        return summary.summaryText;
      }
      if (this.allSessionSummariesOlderThan30Days()) {
        // If account summary text is null, and there are session summaries, but they are older than 30 days, display this text
        return 'No customer self-service activity in the last 30 days.';
      }
      // If account summary text is null, and at least one of the session summaries are recent, display this text
      return 'No new customer alerts.';
    }
    // Display this text if there is no summary at all
    return 'No customer self-service activity in the last 30 days.';
  }

  getSessionSummaryText(summary?: SessionSummary): string {
    if (summary) {
      if (summary.summaryText?.length > 0) {
        return summary.summaryText;
      }
    }
    return 'No summary to show';
  }

  // Use central time for comed
  get currentTimeZone(): string {
    return this.opCo.toLowerCase() === 'comed' ? 'America/Chicago' : 'America/New_York';
  }

  isActivityResolved(session: SessionSummary): boolean {
    if (!session || !session.summaryStatus) {
      return false;
    }

    return session.summaryStatus === 'ResolvedFailure';
  }

  getActivityStatusIcon(session: SessionSummary): { icon: string; cssClass: string; ariaLabel: string } | null {
    if (!session || !session.summaryStatus) {
      return null;
    }

    const status = session.summaryStatus;

    if (status === 'Success') {
      // Success - green checkmark
      return {
        icon: 'euds/check-circle-inside',
        cssClass: 'stroke-success-primary',
        ariaLabel: 'Completed Successfully'
      };
    } else if (status === 'ResolvedFailure') {
      // Resolved error - greyed out alert
      return {
        icon: 'feather/alert-triangle',
        cssClass: 'stroke-neutral-dark',
        ariaLabel: 'Issue Resolved'
      };
    } else if (status === 'FunctionalFailure' || status === 'TechnicalFailure') {
      // Open error - orange alert
      return {
        icon: 'feather/alert-triangle',
        cssClass: 'stroke-attention-primary',
        ariaLabel: 'Action Required'
      };
    } else if (status === 'Abandoned') {
      // Abandoned session - orange alert
      return {
        icon: 'feather/alert-triangle',
        cssClass: 'stroke-attention-primary',
        ariaLabel: 'Session Abandoned'
      };
    }

    return null;
  }

  showFeedbackIcons(session: SessionSummary): boolean {
    return session.summaryStatus !== 'ResolvedFailure' && session.summaryStatus !== 'Success';
  }
}
